export default function EvictionConsiderations() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-scales-3-line"></i>
              <span>Legal Process</span>
            </div>
            
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Eviction Considerations When Selling
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Deciding whether to pursue evictions before selling requires careful consideration of timing, costs, and impact on property value. Sometimes eviction is necessary; other times it's counterproductive.
            </p>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-red-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Critical Timing Issue</h4>
                  <p className="text-red-800 text-sm leading-relaxed">
                    Evictions typically take 3-6 months from start to finish. If you're planning to sell within 6 months, starting an eviction may not be completed before closing, leaving you with a unit in legal limbo that significantly reduces value.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">When to Pursue Eviction Before Selling</h3>

            <div className="space-y-4 mb-8">
              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Chronic Non-Payment (3+ Months Arrears)</h4>
                    <p className="text-slate-600 text-sm">
                      Tenants who haven't paid rent in 3+ months are unlikely to catch up. Eviction is necessary to stop losses and demonstrate to buyers you're addressing the problem.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Serious Lease Violations</h4>
                    <p className="text-slate-600 text-sm">
                      Illegal activities, property damage, or safety violations require immediate eviction regardless of sale timing. These situations create liability and scare away buyers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Long Timeline Before Sale (12+ Months)</h4>
                    <p className="text-slate-600 text-sm">
                      If you're planning to sell in a year or more, you have time to complete evictions and re-lease units at market rates, maximizing property value.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">When to Avoid Eviction Before Selling</h3>

            <div className="space-y-4">
              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Short Timeline (Less Than 6 Months)</h4>
                    <p className="text-slate-600 text-sm">
                      Starting eviction when you plan to sell soon creates uncertainty. Buyers prefer vacant units or occupied units with good tenants over units in eviction proceedings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Minor Payment Issues</h4>
                    <p className="text-slate-600 text-sm">
                      Tenants who are occasionally late but eventually pay don't warrant eviction. The cost and time of eviction outweigh the benefit, especially during a sale.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Tenant-Friendly Jurisdictions</h4>
                    <p className="text-slate-600 text-sm">
                      In cities with strong tenant protections (NYC, SF, LA), evictions can take 12+ months. Cash-for-keys buyouts are usually faster and cheaper.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/e07aa3a6e7c61eb977cb1d82a27eea3a.jpg"
                alt="Eviction considerations and legal process during apartment building sale"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100 max-w-xs">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-red-600 text-2xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">3-6</div>
                  <div className="text-sm text-slate-600">Months Average</div>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Typical eviction timeline from filing to tenant removal
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Alternatives to Eviction During Sale Process</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-hand-coin-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Cash-for-Keys Buyout</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Offer $1,000-$5,000 for tenant to voluntarily vacate within 30 days. Much faster and cheaper than eviction, and avoids legal complications.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Cost:</span>
                  <span className="font-semibold text-slate-900">$2,000-$5,000</span>
                </div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Timeline:</span>
                  <span className="font-semibold text-slate-900">2-4 weeks</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Success Rate:</span>
                  <span className="font-semibold text-green-600">70-85%</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-file-edit-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Lease Modification Agreement</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Negotiate payment plan or lease amendment to resolve issues without eviction. Keeps unit occupied and demonstrates problem-solving to buyers.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Cost:</span>
                  <span className="font-semibold text-slate-900">$0-$500</span>
                </div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Timeline:</span>
                  <span className="font-semibold text-slate-900">1-2 weeks</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Success Rate:</span>
                  <span className="font-semibold text-green-600">50-65%</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-arrow-left-right-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Transfer Problem to Buyer</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Fully disclose problem tenant to buyer and negotiate price reduction. Let buyer handle eviction post-closing with their own legal team.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Cost:</span>
                  <span className="font-semibold text-slate-900">$5K-$15K discount</span>
                </div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Timeline:</span>
                  <span className="font-semibold text-slate-900">Immediate</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Buyer Acceptance:</span>
                  <span className="font-semibold text-amber-600">Varies</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-wallet-3-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Escrow Holdback for Eviction</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Agree to hold funds in escrow to cover eviction costs and lost rent. Buyer handles eviction, and you reimburse from escrow if needed.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Cost:</span>
                  <span className="font-semibold text-slate-900">$10K-$20K holdback</span>
                </div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">Timeline:</span>
                  <span className="font-semibold text-slate-900">Post-closing</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Risk:</span>
                  <span className="font-semibold text-amber-600">Moderate</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Impact of Eviction Status on Property Value</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Unit Status</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Buyer Perception</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Value Impact</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Recommended Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Good tenant, current on rent</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-checkbox-circle-line"></i>
                      Excellent
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Full value</td>
                  <td className="py-4 px-4 text-slate-600">Retain and maintain relationship</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Vacant unit, ready to lease</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-teal-600 font-semibold">
                      <i className="ri-checkbox-line"></i>
                      Good
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-5% to -10%</td>
                  <td className="py-4 px-4 text-slate-600">Lease before closing if possible</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Problem tenant, no eviction started</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <i className="ri-error-warning-line"></i>
                      Concerning
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-10% to -15%</td>
                  <td className="py-4 px-4 text-slate-600">Cash-for-keys or full disclosure</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Eviction in progress (early stage)</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      Negative
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-15% to -25%</td>
                  <td className="py-4 px-4 text-slate-600">Complete eviction or negotiate buyout</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-700">Eviction completed, unit vacant</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-teal-600 font-semibold">
                      <i className="ri-checkbox-line"></i>
                      Acceptable
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-5% to -10%</td>
                  <td className="py-4 px-4 text-slate-600">Clean, repair, and market unit</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-slate-600 mt-6 italic">
            * Value impacts are per-unit estimates and compound when multiple units have issues
          </p>
        </div>
      </div>
    </section>
  );
}
